import React, { ReactElement, useMemo } from 'react';
import styled from '@emotion/styled';
import Avatar from '../../../atoms/Avatar';
import { Link } from 'react-router-dom';
import { spacing, getBorders, type, getTextColor, getTextType, getBackgroundTheme } from '../../../style';
import { displayUserLocation, tsSince, renderAvatar } from '../../../common';
import { isDarkMode, lazilyEvaluateVal } from '../../../utils/helpers';
import { PrivatePhotoIcon, PublicPhotoIcon, StarIcon } from '../../../atoms/icons/favoriteIcons';
import OfferActionButton from './OfferActionButton';
import { auth10tixVerification } from '../../../config/Master';
import useMixPanel from '../../../hooks/useMixPanel';
import { EVENT_NAMES, TRACK_PROFILE_VIEWS_FOR_OFFER_MADE } from '../../../constants/mixpanel';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import useMediaBreakpoints from '../../../hooks/useMediaBreakpoints';
import breakpoints from '../../../theme/material/breakpoints';
import { CustomStyledProps } from 'style/style';
import VerifiedBadgeTooltip from '../../common/VerifiedBadgeTooltip';
import { FONT_FAMILIES } from 'theme/material/constants';

const ResultItem = styled('div')(
  () => ({
    ...getTextColor().DARKER,
    display: 'inline-flex',
    backgroundColor: getBackgroundTheme().block,
    flex: '1 0 100%',
    position: 'relative',
    [breakpoints.down(639)]: {
      flex: '1 0 40%',
    },
    paddingBottom: 20,
    paddingTop: 20,
    borderBottom: getBorders().PRIMARY,
  }),
  ({ backgroundColor }: CustomStyledProps) => ({
    backgroundColor: `${backgroundColor}`,
  })
);

const WrapTitle = styled('div')(() => ({
  width: '100%',
  color: isDarkMode() ? '#FFFFFF' : '#212121',
  display: 'flex',
  alignItems: 'center',
  [breakpoints.down(639)]: {
    alignItems: 'inherit',
  },
}));

const Title = styled('div')(() => ({
  ...type.title.XS,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  fontFamily: FONT_FAMILIES.secondary,
  letterSpacing: '-0.5px',
  [breakpoints.down(639)]: {
    fontSize: 16,
  },
}));

const Meta = styled('div')(() => ({
  ...getTextType().caption,
  letterSpacing: '-0.3px',
  ...spacing.MARGIN_BOTTOM_SM,
  ...getTextColor().LIGHT_GRAY,
  [breakpoints.down(639)]: {
    fontSize: 10,
    maxWidth: 94,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    ...spacing.MARGIN_BOTTOM_XS,
  },
  [breakpoints.down(360)]: {
    maxWidth: 70,
  },
}));

const Details = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  flexGrow: 1,
  marginRight: 12,
  ...getTextColor().caption,
  height: 100,
  [breakpoints.down(768.05)]: {
    maxWidth: 160,
  },
  [breakpoints.down(639.05)]: {
    height: 64,
    maxWidth: 140,
    marginRight: 6,
  },
  [breakpoints.down(374.05)]: {
    maxWidth: 80,
  },
}));

const Wrapper = styled(Link)({
  flexGrow: 1,
  display: 'flex',
  marginLeft: 20,
  [breakpoints.down(639)]: {
    marginLeft: 10,
  },
});

const Timestamp = styled('div')(() => ({
  fontSize: 14,
  ...getTextColor().LIGHT_GRAY,
  letterSpacing: '-0.3px',
  [breakpoints.down(639)]: {
    fontSize: 10,
    marginTop: 0,
  },
}));

const PhotoCountDetails = styled('div')();

const PhotoCount = styled('span')({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: 5,
  [breakpoints.down(639.05)]: {
    gap: 3,
  },
});

const StyledPhotoCount = styled('span')({
  ...getTextColor().LIGHT_GRAY,
  fontSize: 12,
  lineHeight: '15px',
  letterSpacing: -0.3,
  [breakpoints.down(639.05)]: {
    fontSize: 11,
  },
});

const StyledPublicPhotoCount = styled('span')({
  borderRight: '1px solid #E21D4A',
  paddingRight: 5,
  ...getTextColor().LIGHT_GRAY,
  fontSize: 12,
  lineHeight: '15px',
  letterSpacing: -0.3,
  [breakpoints.down(639.05)]: {
    fontSize: 11,
  },
});

const Icon = styled('span')({
  height: 11,
  width: 12,
  fontSize: 0,
  '& svg': {
    height: 11,
    width: 12,
  },
  [breakpoints.down(639.05)]: {
    height: 10,
    width: 11,
    '& svg': {
      height: 10,
      width: 11,
    },
  },
});

const OnlineDot = styled('div')({
  height: '8px',
  width: '8px',
  backgroundColor: 'lightgreen',
  borderRadius: '50%',
  display: 'inline-block',
});

const StyledStatusBannerText = styled('span')(
  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  () => ({
    fontWeight: 600,
    fontStyle: 'italic',
    lineHeight: '19px',
    letterSpacing: '-0.3px',
    display: 'inline-block',
    position: 'relative',
    '::after': {
      content: '"•"',
      display: 'inline-block',
      ...getTextColor().DARKER,
      margin: '0 4px',
      float: 'right',
    },
    svg: {
      height: 12,
    },
    [breakpoints.down(639)]: {
      fontSize: 10,
      lineHeight: '15px',
      minWidth: 110,
      svg: {
        display: 'none',
      },
    },
    [breakpoints.down(360)]: {
      maxWidth: 70,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
    },
  }),
  ({ customColor }: CustomStyledProps) => ({
    color: `${customColor}`,
  })
);

const VerifiedBadge = styled('span')({
  marginLeft: 4,
  zIndex: 2,
  position: 'relative',
  top: 6,
  [breakpoints.down(768.05)]: {
    top: 4,
  },
});

interface OfferProfileListViewProps {
  offerKey: any;
  profile: any;
  title: any;
  hashId: any;
  isFavoritedMe: any;
  isMatched: any;
  timestamp: any;
  offer: any;
  pageTitle: any;
}

const OfferProfileListView = ({
  offerKey,
  profile,
  title,
  hashId,
  isFavoritedMe,
  isMatched,
  timestamp,
  offer,
  pageTitle,
}: OfferProfileListViewProps): ReactElement => {
  const user = useSelector((state: RootState) => state.profile);
  const { isMobile } = useMediaBreakpoints();

  const { trackProfileView, setMixpanelDataByKeyHandler } = useMixPanel();
  const getInfoText = (profile, otherProfile) => {
    if (!profile || !otherProfile) return '';

    const userLoc = {
      country: profile.country,
      region: profile.region,
      city: profile.city,
    };

    const otherUserLoc = {
      country: otherProfile.country,
      region: otherProfile.region,
      city: otherProfile.city,
    };

    return `${otherProfile.age} • ${displayUserLocation(userLoc, otherUserLoc)}`;
  };

  const getStatusBanner = (isCustomBackground = false) => {
    const darkMode = isDarkMode();

    if (isMatched) {
      if (isCustomBackground) {
        return darkMode ? '#432C31' : '#FEF4F6';
      }

      return (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <StyledStatusBannerText data-test-id="fav-banner-mutual-favorite" customColor="#E21D4A">
          <StarIcon isActive={false} fill="#E21D4A" /> Mutual Favorite!
        </StyledStatusBannerText>
      );
    }

    if (isFavoritedMe) {
      if (isCustomBackground) {
        return darkMode ? '#432C31' : '#FEF4F6';
      }

      return (
        // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
        <StyledStatusBannerText data-test-id="fav-banner-favorited-you" customColor="#E21D4A">
          <StarIcon isActive={false} fill="#E21D4A" /> Favorited You!
        </StyledStatusBannerText>
      );
    }

    if (isCustomBackground) {
      return darkMode ? 'transparent' : 'rgba(255, 255, 255, 1)';
    }

    return '';
  };

  const profileData = profile.profile.data;

  const avatar =
    lazilyEvaluateVal(profileData, 3, false, 'avatar', 'data', 'urls') && renderAvatar(profileData.avatar.data.urls);

  const photoCount = parseInt(profileData.approved_public_photos_count);
  const privatePhotoCount = profileData.private_photos_count ? parseInt(profileData.private_photos_count) : 0;
  const userVerified = profileData.id_verified;
  const isSelfieVerified = profileData.selfie_verified;

  const trackCardClickEvent = () => {
    trackProfileView({ Source: pageTitle, User: hashId });

    setMixpanelDataByKeyHandler({
      key: EVENT_NAMES.OFFER_MADE,
      payload: {
        source: user.isGenerous
          ? TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewReceivedRequest
          : TRACK_PROFILE_VIEWS_FOR_OFFER_MADE.profileViewReceivedOffers,
      },
    });
    return true;
  };

  const memoizedProfileDetails = useMemo(
    () => (
      <Wrapper to={`/profile/${hashId}`} onClick={trackCardClickEvent}>
        <Avatar
          src={avatar}
          width="100px"
          alt="Avatar photo"
          customStyle={{
            ...spacing.MARGIN_RIGHT_MD,
            flex: '0 0 100px',
            height: 100,
            [breakpoints.down(639)]: {
              flex: '0 0 65px',
              width: 65,
              height: 65,
              marginRight: 8,
            },
            border: isMatched ? '1px solid #E21D4A' : '1px solid rgba(218, 225, 233, 1)',
          }}
        />
        <Details>
          <div>
            <WrapTitle>
              <Title>{title}</Title>
              {auth10tixVerification && userVerified && (
                <VerifiedBadge data-test-id="data-verified-badge">
                  <VerifiedBadgeTooltip
                    showToolTip={true}
                    isIdVerified={userVerified}
                    isSelfieVerified={isSelfieVerified}
                  />
                </VerifiedBadge>
              )}
            </WrapTitle>
            <Meta>
              {profileData.online ? <OnlineDot /> : ''} {getInfoText(user, profileData)}
            </Meta>
            <PhotoCountDetails data-test-id="data-member-photo-count">
              <PhotoCount>
                <Icon data-test-id="data-camera-icon">
                  <PublicPhotoIcon />
                </Icon>
                <StyledPublicPhotoCount>{photoCount}</StyledPublicPhotoCount>
                <Icon data-test-id="data-eye-hidden-icon">
                  <PrivatePhotoIcon />
                </Icon>
                <StyledPhotoCount>{privatePhotoCount}</StyledPhotoCount>
              </PhotoCount>
            </PhotoCountDetails>
          </div>
          <Timestamp>
            {!isMobile && getStatusBanner()}
            {tsSince(timestamp)}
          </Timestamp>
        </Details>
      </Wrapper>
    ),
    [hashId, avatar, title, profileData, isMatched, userVerified, photoCount, privatePhotoCount, timestamp]
  );

  return (
    <ResultItem className="css-offers-list-item" backgroundColor={getStatusBanner(true)}>
      {memoizedProfileDetails}
      <OfferActionButton
        offer={offer}
        otherAccount={profile}
        isMatched={isMatched}
        offerKey={offerKey}
        viewType="list"
        isSuggestRequest={offer.data.suggest}
        pageTitle={pageTitle}
      />
    </ResultItem>
  );
};

export default OfferProfileListView;
